import type { ErrorComponentProps } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { ReservationProvider } from "@/context/ReservationContext";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { initAnalytics } from "@/lib/analytics";
import { Toaster } from "@/components/ui/sonner";


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Plunkets Pub Dublin" },
      { name: "description", content: "Plunkets Pub — a Dublin city centre bar for sport, pool, cocktails and live music." },
      { name: "author", content: "Plunkets Pub" },
      { name: "theme-color", content: "#0F1E22" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { property: "og:site_name", content: "Plunkets Pub Dublin" },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_IE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@plunketspub" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Preload the Google Fonts CSS without blocking render; a tiny inline
      // script below promotes it to a real stylesheet after first paint.
      {
        rel: "preload",
        as: "style" as unknown as undefined,
        href: "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,800;0,900;1,600&family=Inter:wght@400;500;600;700&display=swap",
      } as never,
    ],
    scripts: [
      {
        children:
          "(function(){var l=document.querySelector('link[rel=preload][as=style][href*=\"fonts.googleapis.com\"]');if(l){l.onload=function(){this.onload=null;this.rel='stylesheet';};if(l.sheet){l.rel='stylesheet';}}})();",
      },
      {
        src: "https://www.googletagmanager.com/gtag/js?id=G-49HQZFMECY",
        async: true,
      },
      {
        children:
          "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', 'G-49HQZFMECY');",
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  // Hide the public WhatsApp button on /go and inside the staff app (/admin, /auth).
  const isQuickLinksPage = useRouterState({
    select: (state) => {
      const p = state.location.pathname;
      return p === "/go" || p === "/auth" || p.startsWith("/admin");
    },
  });

  useEffect(() => {
    initAnalytics();
  }, []);


  return (
    <QueryClientProvider client={queryClient}>
      <ReservationProvider>
        {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
        <Outlet />
        {!isQuickLinksPage && <FloatingWhatsApp />}
        <Toaster richColors position="top-center" />
      </ReservationProvider>
    </QueryClientProvider>
  );
}

